import React from 'react';
import clsx from 'clsx';
import StoreLogo from '../../components/StoreLogo';
import dayjs from 'dayjs';

type OfferItemProps = {
    storeName: string;
    storeProductId: number | string;
    marketplace?: boolean;
    salePrice: number;           // cents
    available: boolean;
    user?: boolean;
    originalLink?: string;
    listPrice?: number | null;   // cents
    shipping?: number | null;    // cents (0 = free)
    updatedAt?: string | Date | null;
    isBest?: boolean;
    isAffiliate?: boolean;
    isOfficial?: boolean;
    currency?: string;
    rating?: number;
    reviewsCount?: number;
    variant?: 'default' | 'compact';
};

const OfferItem: React.FC<OfferItemProps> = ({
    storeName,
    storeProductId,
    marketplace = false,
    salePrice,
    available,
    user = false,
    originalLink,
    listPrice = null,
    shipping = null,
    updatedAt = null,
    isBest = false,
    isAffiliate = false,
    isOfficial = false,
    currency = 'CAD',
    rating,
    reviewsCount,
    variant = 'compact',
}) => {
    const url = user && originalLink ? originalLink : `/link/${storeProductId}`;
    const rel = 'noopener nofollow' + (isAffiliate ? ' sponsored' : '');

    const fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency });
    const priceStr = salePrice > 0 ? fmt.format(salePrice / 100) : '—';
    const listPriceStr =
        listPrice && listPrice > salePrice ? fmt.format(listPrice / 100) : null;
    const shippingStr =
        shipping === 0
            ? 'Free shipping'
            : typeof shipping === 'number' && shipping > 0
                ? `+ ${fmt.format(shipping / 100)} shipping`
                : null;
    const updatedText = updatedAt
        ? dayjs(updatedAt).local().format('MMM D, YYYY')
        : null;

    const availability =
        available ? 'http://schema.org/InStock' : 'http://schema.org/OutOfStock';

    const baseCard = clsx(
        'rounded-xl border border-gray-200 bg-white',
        'transition-colors hover:border-blue-300'
    );

    if (variant === 'compact') {
        return (
            <article className={clsx(baseCard, 'p-3 md:p-4')} itemScope itemType="http://schema.org/Offer">
                <meta itemProp="priceCurrency" content={currency} />
                <meta itemProp="availability" content={availability} />
                <link itemProp="url" href={url} />

                <div className="flex justify-between items-center md:gap-4">
                    {/* Магазин + бейджи */}
                    <a href={url} target="_blank" rel={rel} className="flex items-center gap-2 md:gap-3 min-w-0">
                        <StoreLogo name={storeName} />
                        <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-2">
                                <span className="truncate text-base md:text-lg font-medium">{storeName}</span>

                                {isOfficial ? (
                                    <span className="px-2 py-[2px] text-[11px] rounded-full bg-emerald-100 text-emerald-700">Official</span>
                                ) : null}

                                {marketplace ? (
                                    <span className="px-2 py-[2px] text-[11px] rounded-full bg-amber-100 text-amber-700">Marketplace</span>
                                ) : null}

                                {(isBest && available) ? (
                                    <span className="px-2 py-[2px] text-[11px] rounded-full bg-blue-100 text-blue-700">Best price</span>
                                ) : null}
                                {(isAffiliate) ? (
                                    <span title="We may earn a commission" className="px-2 py-[2px] text-[11px] rounded-full bg-emerald-100 text-emerald-700">Affiliate link</span>
                                ) : null}
                            </div>

                            <div className="mt-0.5 text-[11px] md:text-xs text-gray-600 flex flex-wrap gap-x-2">
                                {updatedText ? <span>Updated {updatedText}</span> : null}
                                {shippingStr ? <span>• {shippingStr}</span> : null}
                                {typeof rating === 'number' ? <span>• ★ {rating.toFixed(1)}</span> : null}
                                {typeof reviewsCount === 'number' ? <span>({reviewsCount})</span> : null}
                            </div>
                        </div>
                    </a>

                    {/* Цена + кнопка */}
                    <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row items-center gap-3 md:gap-4">
                        <div className="text-right">
                            {listPriceStr ? (
                                <div className="text-xs text-gray-500 line-through">{listPriceStr}</div>
                            ) : null}
                            <div
                                className={clsx('font-semibold', available ? 'text-xl md:text-2xl' : 'text-lg text-gray-700')}
                                itemProp="price"
                                content={(salePrice / 100).toFixed(2)}
                            >
                                {priceStr}
                            </div>
                        </div>

                        <a
                            href={url}
                            target="_blank"
                            rel={rel}
                            className={clsx(
                                'px-4 md:px-5 py-2 rounded-md text-sm md:text-base whitespace-nowrap',
                                available ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                            )}
                            aria-disabled={!available}
                        >
                            {available ? 'View offer' : 'Out of stock'}
                            <span className="sr-only"> (opens in new tab)</span>
                        </a>
                    </div>
                </div>
            </article>
        );
    }

    // можно оставить fallback-вариант, если используете
    return (
        <article className={clsx(baseCard, 'p-3 md:p-4')} />
    );
};

export default OfferItem;